/**
 * Connecting a Claude account, and what the surface says once one is.
 *
 * @module
 */
import { CmmdClaudeCredentialState, cmmdApiClient } from "../../environments/cmmdApi";
import {
  CloudProviderSignInController,
  providerAccountsQueryKey,
} from "../../hooks/useCloudProviderSignIn";
import { useProviderAuth } from "../../hooks/useProviderAuth";
import { ensureLocalApi } from "../../localApi";
import { applyProvidersUpdated } from "../../rpc/serverState";
import { toastManager } from "../ui/toast";
import { ClaudeCloudAccountSection } from "./ClaudeCloudAccountSection";
import { ClaudeLocalAuthOptions } from "./ClaudeLocalAuthOptions";
import { ProviderAccountListItem } from "./ProviderAccountManagePanel";
import {
  CmmdProviderAccountInventoryAccount,
  resolveClaudeCloudAccountState,
} from "./providerAccountState";
import { ServerProvider } from "@cmmd-forge/contracts";
import { UnifiedSettings } from "@cmmd-forge/contracts/settings";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

// moved verbatim from the 5377-line SettingsPanels.tsx; complexity is
// unchanged, and fallow reads a relocated function as a new one
// fallow-ignore-next-line complexity
export function ClaudeAuthenticationOptions({
  provider,
  settings,
  updateSettings,
  cloudSignIn,
  claudeCloudActive,
  providerAccounts,
}: {
  provider: ServerProvider | undefined;
  settings: Pick<UnifiedSettings, "providers">;
  updateSettings: (patch: Partial<UnifiedSettings>) => void;
  cloudSignIn: CloudProviderSignInController<CmmdClaudeCredentialState>;
  claudeCloudActive: boolean;
  providerAccounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
}) {
  const providerAuth = useProviderAuth({ settings, updateSettings });
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isClaudeAccountsDialogOpen, setIsClaudeAccountsDialogOpen] = useState(false);
  const [isClaudeCloudSwitching, setIsClaudeCloudSwitching] = useState(false);
  const isConnected = provider?.auth.status === "authenticated";
  const {
    cloudConnected,
    cloudManageable,
    hasKnownAccount,
    cloudAccountEmail,
    cloudPlanLabel,
    claudeAccountSummary,
    claudeAccountRows,
  } = resolveClaudeCloudAccountState({
    claudeCloudActive,
    connection: cloudSignIn.connection,
    providerAccounts,
  });
  const [selectingClaudeDefaultAccountId, setSelectingClaudeDefaultAccountId] = useState<
    string | null
  >(null);
  const [removingClaudeAccountId, setRemovingClaudeAccountId] = useState<string | null>(null);

  const removeLinkedClaudeAccount = useCallback(
    async (id: string) => {
      setRemovingClaudeAccountId(id);
      try {
        await cmmdApiClient.removeLinkedProviderAccount(id);
        await queryClient.invalidateQueries({ queryKey: providerAccountsQueryKey });
        toastManager.add({ type: "success", title: "Account removed" });
      } catch (error) {
        toastManager.add({
          type: "error",
          title: "Couldn't remove account",
          description: error instanceof Error ? error.message : "Try again from Settings.",
        });
      } finally {
        setRemovingClaudeAccountId(null);
      }
    },
    [queryClient],
  );

  const selectClaudeDefaultAccount = useCallback(
    async (account: ProviderAccountListItem) => {
      if (account.isDefaultForNewChats || account.needsReconnect) return;
      setSelectingClaudeDefaultAccountId(account.id);
      try {
        const inventory = await cmmdApiClient.setProviderAccountDefault({
          provider: "claudeAgent",
          providerAccountId: account.id,
        });
        queryClient.setQueryData(providerAccountsQueryKey, inventory);
        await queryClient.invalidateQueries({ queryKey: providerAccountsQueryKey });
        toastManager.add({
          type: "success",
          title: "Default Claude account updated",
          description: "New Claude agent runs will use the selected account.",
        });
      } catch (error) {
        toastManager.add({
          type: "error",
          title: "Could not update Claude account",
          description: error instanceof Error ? error.message : "Try again from Settings.",
        });
      } finally {
        setSelectingClaudeDefaultAccountId(null);
      }
    },
    [queryClient],
  );

  useEffect(() => {
    if (!isClaudeAccountsDialogOpen || isClaudeCloudSwitching || cloudManageable) return;
    setIsClaudeAccountsDialogOpen(false);
  }, [cloudManageable, isClaudeAccountsDialogOpen, isClaudeCloudSwitching]);

  useEffect(() => {
    if (isClaudeCloudSwitching && cloudSignIn.phase?.kind === "done") {
      setIsClaudeCloudSwitching(false);
    }
  }, [cloudSignIn.phase?.kind, isClaudeCloudSwitching]);
  // Card 1359 (Claude) — on a cloud surface `claude auth login` can't run
  // locally, so route personal sign-in through the cloud auth-VM flow (which
  // also renders the connected-state + disconnect off the CMMD user store). The
  // build flag is on for cloud surfaces and off for desktop, where local sign-in
  // works. Local auth still wins (that path returns first, below).
  // Gating + controller live in useClaudeCloudSignIn so this god file does not
  // grow another orchestration concern.
  const disconnectClaude = async (): Promise<void> => {
    setIsDisconnecting(true);
    try {
      const payload = await ensureLocalApi().server.disconnectProviderAuth({
        provider: "claudeAgent",
      });
      applyProvidersUpdated(payload);
      toastManager.add({
        type: "success",
        title: "Claude account disconnected",
        description: "This runtime no longer uses your personal Claude subscription.",
      });
    } catch (error) {
      toastManager.add({
        type: "error",
        title: "Could not disconnect Claude",
        description: error instanceof Error ? error.message : "Try again from Settings.",
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  if (isConnected) {
    return (
      <div className="min-w-0">
        <p className="text-xs leading-5 text-muted-foreground">
          Personal Claude subscription connected. Forge uses it where supported.
        </p>
        <button
          type="button"
          disabled={isDisconnecting}
          onClick={() => void disconnectClaude()}
          className="mt-2 inline-flex h-8 items-center rounded-md border border-border bg-background px-3 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isDisconnecting ? "Disconnecting…" : "Disconnect"}
        </button>
      </div>
    );
  }

  // Cloud surface: the auth-VM flow owns connect + connected-state + disconnect
  // (read from the CMMD user store), so the local paths below don't apply.
  if (claudeCloudActive) {
    return (
      <ClaudeCloudAccountSection
        cloudSignIn={cloudSignIn}
        cloudConnected={cloudConnected}
        cloudManageable={cloudManageable}
        hasKnownAccount={hasKnownAccount}
        cloudAccountEmail={cloudAccountEmail}
        cloudPlanLabel={cloudPlanLabel}
        claudeAccountSummary={claudeAccountSummary}
        claudeAccountRows={claudeAccountRows}
        isClaudeAccountsDialogOpen={isClaudeAccountsDialogOpen}
        setIsClaudeAccountsDialogOpen={setIsClaudeAccountsDialogOpen}
        isClaudeCloudSwitching={isClaudeCloudSwitching}
        setIsClaudeCloudSwitching={setIsClaudeCloudSwitching}
        selectingClaudeDefaultAccountId={selectingClaudeDefaultAccountId}
        onSelectClaudeDefaultAccount={(account) => void selectClaudeDefaultAccount(account)}
        onRemoveClaudeAccount={(id) => void removeLinkedClaudeAccount(id)}
        removingClaudeAccountId={removingClaudeAccountId}
      />
    );
  }

  return <ClaudeLocalAuthOptions providerAuth={providerAuth} code={code} setCode={setCode} />;
}

// Keep the existing Codex auth state machine in place for this provider-account reuse PR.
// Splitting the full flow belongs in a dedicated SettingsPanels decomposition.
